/**
 * 0.46.40 - Guide card (Henaket "card guide").
 *
 * A picture on the left (80px wide, the card's inner height; 80x136,
 * centred above the text on phones), then an optional status badge, a title
 * drawn as a link and a short text. The whole card is clickable: a
 * click anywhere on it follows the title's link. The title is the one
 * real link (or button, when there is no address), announced by a
 * screen reader. 0.46.61 - The keyboard stops on the whole card first
 * (Enter or Space opens it), then on the title's link.
 *
 * Henaket spec: up to 768px wide, padding 16px 24px, gap 24px between
 * the picture and the content, 8px between the content's parts, white
 * fill, Text 300 stroke, 4px corners. The badge has the Information
 * card's size, style and colors. Long titles and texts wrap and the
 * card grows taller. Below 768px the picture moves above the text.
 *
 * With no picture its area is kept empty, so cards in a list
 * keep their text aligned.
 */
import React, { useRef } from 'react';
import styles from './GuideCard.module.css';
import { chipStyle } from '../lib/readableText';
import { cardColorVars } from '../lib/cardColors';

const _s = (v) => (v == null ? '' : (typeof v === 'string' ? v : String(v)));

export default function GuideCard({
  badge = '',
  badgeColor = '',
  // 0.46.43 - The badge's outline color (empty: none).
  badgeStroke = '',
  // 0.48.05 - The badge's text color (empty: automatic).
  badgeTextColor = '',
  title = '',
  text = '',
  imageSrc = '',
  imageAlt = '',
  href = '',
  newTab = false,
  onActivate = null,
  // True on the editor canvas: drawn, never followed.
  inert = false,
  // 0.47.20 - The card's fill and stroke (empty: Henaket).
  cardColor = '',
  cardStroke = '',
  // 0.47.26 - 'contain' (the whole picture) or 'cover' (fill and crop).
  imageFit = 'contain',
}) {
  const linkRef = useRef(null);
  const cardRef = useRef(null);
  const badgeText = _s(badge);
  const titleText = _s(title);
  const bodyText = _s(text);
  const url = _s(href).trim();

  const activate = (ev) => {
    if (inert) { if (ev) ev.preventDefault(); return; }
    if (typeof onActivate === 'function') onActivate();
    if (!url && ev) ev.preventDefault();
  };

  // A click anywhere on the card follows the title's link, except a
  // click that ends a text selection (copying a sentence from the card
  // should not open it) and a click on the link itself.
  const onCardClick = (ev) => {
    if (inert) return;
    if (ev.target.closest && ev.target.closest('a, button')) return;
    const sel = typeof window !== 'undefined' && window.getSelection
      ? String(window.getSelection()) : '';
    if (sel) return;
    if (linkRef.current) linkRef.current.click();
  };

  const titleNode = url && !inert
    ? (
      <a ref={linkRef} className={styles.title} href={url}
         target={newTab ? '_blank' : undefined}
         rel={newTab ? 'noopener noreferrer' : undefined}
         onClick={activate}>
        {titleText}
      </a>
    )
    : (
      <button ref={linkRef} type="button" className={styles.title}
              onClick={activate} tabIndex={inert ? -1 : undefined}>
        {titleText}
      </button>
    );

  /* 0.46.43 - Pressing the badge or the title focuses the title's link,
     so the badge and the title light up together (the magenta block).
     The mouse-down default is prevented so the browser does not move
     focus to the card instead (Safari never focuses a clicked link).
     A press anywhere else focuses the card itself, which shows the
     platform's general ring. 0.46.61 - The card is a Tab stop too
     (tabIndex 0, was -1), before the title's link. */
  const onHeadMouseDown = (ev) => {
    if (inert || ev.button !== 0) return;
    ev.preventDefault();
    if (linkRef.current) linkRef.current.focus({ preventScroll: true });
  };
  const onHeadClick = (ev) => {
    if (inert) return;
    if (ev.target.closest && ev.target.closest('a, button')) return;
    ev.stopPropagation();
    if (linkRef.current) linkRef.current.click();
  };

  /* 0.46.61 - The keyboard reaches the whole card first: Tab stops on
     the card (the general ring), Enter or Space opens it as a click
     does, and the next Tab goes on to the title's link (the magenta
     block). Keys pressed on the link itself are the link's own. */
  const onCardKeyDown = (ev) => {
    if (inert || ev.target !== ev.currentTarget) return;
    if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
      ev.preventDefault();
      if (linkRef.current) linkRef.current.click();
    }
  };

  /* 0.46.65 - The wrapper is the card's container, so the card can
     switch to its narrow layout (picture above the text) by its OWN
     width, not only the screen's: in 5 columns or fewer of a wide
     page too. */
  return (
    <div className={styles.wrap} data-guide-wrap="">
    <div ref={cardRef} data-guide-card="" className={`${styles.card} ${inert ? '' : styles.clickable}`}
         style={cardColorVars(cardColor, cardStroke)}
         tabIndex={inert ? undefined : 0}
         role={inert ? undefined : 'group'}
         aria-label={inert || !titleText ? undefined : titleText}
         onKeyDown={onCardKeyDown}
         onClick={onCardClick}>
      <div className={styles.image}>
        {_s(imageSrc) !== '' && (
          <img className={styles.imageFill} src={imageSrc} alt={_s(imageAlt)}
               style={imageFit === 'cover' ? { objectFit: 'cover', objectPosition: 'center' } : undefined}
               loading="lazy"
               onError={(ev) => { ev.currentTarget.style.visibility = 'hidden'; }} />
        )}
      </div>
      <div className={styles.content}>
        {(badgeText !== '' || titleText !== '') && (
          <div className={styles.head} onMouseDown={onHeadMouseDown} onClick={onHeadClick}>
            {badgeText !== '' && (
              <span className={styles.badge} style={chipStyle(badgeColor, badgeStroke, badgeTextColor)}>{badgeText}</span>
            )}
            {titleText !== '' && <h3 className={styles.heading}>{titleNode}</h3>}
          </div>
        )}
        {bodyText !== '' && <p className={styles.text}>{bodyText}</p>}
      </div>
    </div>
    </div>
  );
}
